import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from '../../../../shared/i18n';
import { ExternalLink } from '../../../../shared/components';
import { InstructionsModal } from './InstructionsModal';
import { getInstructionImages, hasInstructions } from './instructionImages';
import { writeText } from '../../../../shared/utils/clipboard';

// localStorage key for storing URL preference
const getPreferenceKey = (activityType) => `powerkid_goto_preference_${activityType}`;

/**
 * CredentialsModal - Shows account credentials (username, password) for external services
 * with copy-to-clipboard functionality and a launch button.
 *
 * @param {Object} props
 * @param {boolean} props.show - Whether to show the modal
 * @param {Function} props.onHide - Callback to close the modal
 * @param {string} props.serviceName - Display name of the service (e.g., "Duolingo")
 * @param {string} props.serviceUsername - The account username
 * @param {string|null} props.servicePassword - The account password (optional)
 * @param {string|null} props.serviceProfileName - The kid profile name within the parent account (optional, used by GetEpic)
 * @param {string} props.activityUrl - URL to launch the service (web)
 * @param {string|null} props.activityAppUrl - App URL scheme to launch the service (mobile app)
 * @param {string} props.activityType - Type of activity (e.g., "duolingo", "codecombat")
 * @param {string} props.activitySubType - Sub-type of activity (e.g., "music", "english")
 * @param {Array<string>} props.courses - Array of course IDs for the activity
 */
export const CredentialsModal = ({
  show,
  onHide,
  serviceName,
  serviceUsername,
  servicePassword,
  serviceProfileName = null,
  activityUrl,
  activityAppUrl = null,
  activityType,
  activitySubType = null,
  courses = []
}) => {
  const { t } = useTranslation();
  const [copiedField, setCopiedField] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Check if this activity supports both web and app URLs
  const hasDualMode = Boolean(activityAppUrl);

  // Get saved preference from localStorage, default to 'web'
  const [urlPreference, setUrlPreference] = useState(() => {
    if (!hasDualMode) return 'web';
    try {
      const saved = localStorage.getItem(getPreferenceKey(activityType));
      return saved || 'web'; // default to web
    } catch {
      return 'web';
    }
  });

  // Save preference to localStorage
  const savePreference = useCallback((preference) => {
    setUrlPreference(preference);
    try {
      localStorage.setItem(getPreferenceKey(activityType), preference);
    } catch {
      // localStorage might be unavailable
    }
    setShowDropdown(false);
  }, [activityType]);

  // Get the URL to use based on preference
  const currentUrl = urlPreference === 'app' ? activityAppUrl : activityUrl;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showDropdown && !e.target.closest('.dropdown')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDropdown]);

  // Check if this activity type has instructions
  const showInstructionsButton = hasInstructions(activityType, activitySubType);

  // Build instruction modal configuration based on activity type/sub-type
  // Template key format: [type, subType].compact.join("-")
  const instructionsConfig = useMemo(() => {
    if (!showInstructionsButton) return null;

    const images = getInstructionImages(activityType, courses, activitySubType);
    const templateKey = [activityType, activitySubType].filter(Boolean).join('-');
    return {
      images,
      title: t('children.activity_sessions.instructions_title', { service: serviceName }),
      testId: `${templateKey}-instructions-modal`
    };
  }, [activityType, activitySubType, courses, showInstructionsButton, t]);

  // Copy text to clipboard with feedback
  const handleCopy = useCallback(async (text, fieldName) => {
    try {
      await writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  }, []);

  if (!show) return null;

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      data-testid="credentials-modal"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header border-0 pb-0">
            <h5 className="modal-title fw-bold">
              {t('children.activity_sessions.credentials_modal.title', { service: serviceName })}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onHide}
              aria-label={t('common.close')}
            ></button>
          </div>
          <div className="modal-body">
            <p className="text-secondary mb-4">
              {t('children.activity_sessions.credentials_modal.description', { service: serviceName })}
            </p>

            {/* Username field */}
            <div className="mb-3">
              <label className="form-label fw-semibold text-secondary small">
                {t('children.activity_sessions.credentials_modal.username_label')}
              </label>
              <div className="d-flex">
                <input
                  type="text"
                  className="form-control rounded-end-0"
                  value={serviceUsername || ''}
                  readOnly
                  data-testid="credentials-username"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-start-0 border-start-0"
                  onClick={() => handleCopy(serviceUsername, 'username')}
                  title={t('common.copy')}
                  data-testid="copy-username-button"
                  style={{ minHeight: 'auto', padding: '0.375rem 0.75rem', width: 'auto' }}
                >
                  {copiedField === 'username' ? (
                    <i className="bi bi-check-lg text-success"></i>
                  ) : (
                    <i className="bi bi-clipboard"></i>
                  )}
                </button>
              </div>
            </div>

            {/* Password field - only show if password exists */}
            {servicePassword && (
              <div className="mb-3">
                <label className="form-label fw-semibold text-secondary small">
                  {t('children.activity_sessions.credentials_modal.password_label')}
                </label>
                <div className="d-flex">
                  <input
                    type="text"
                    className="form-control rounded-end-0"
                    value={servicePassword}
                    readOnly
                    data-testid="credentials-password"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-start-0 border-start-0"
                    onClick={() => handleCopy(servicePassword, 'password')}
                    title={t('common.copy')}
                    data-testid="copy-password-button"
                    style={{ minHeight: 'auto', padding: '0.375rem 0.75rem', width: 'auto' }}
                  >
                    {copiedField === 'password' ? (
                      <i className="bi bi-check-lg text-success"></i>
                    ) : (
                      <i className="bi bi-clipboard"></i>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Profile name field - only show if a profile name is configured (e.g., GetEpic) */}
            {serviceProfileName && (
              <div className="mb-3">
                <label className="form-label fw-semibold text-secondary small">
                  {t('children.activity_sessions.credentials_modal.profile_name_label')}
                </label>
                <div className="d-flex">
                  <input
                    type="text"
                    className="form-control rounded-end-0"
                    value={serviceProfileName}
                    readOnly
                    data-testid="credentials-profile-name"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-start-0 border-start-0"
                    onClick={() => handleCopy(serviceProfileName, 'profile_name')}
                    title={t('common.copy')}
                    data-testid="copy-profile-name-button"
                    style={{ minHeight: 'auto', padding: '0.375rem 0.75rem', width: 'auto' }}
                  >
                    {copiedField === 'profile_name' ? (
                      <i className="bi bi-check-lg text-success"></i>
                    ) : (
                      <i className="bi bi-clipboard"></i>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer border-0 pt-0">
            {showInstructionsButton && (
              <button
                type="button"
                className="btn btn-info"
                onClick={() => setShowInstructions(true)}
                data-testid="show-instructions-button"
              >
                <i className="bi bi-question-circle me-1"></i>
                {t('children.activity_sessions.credentials_modal.how_to_use')}
              </button>
            )}

            {/* Single mode: simple button (no app URL available) */}
            {!hasDualMode && (
              <ExternalLink
                href={activityUrl}
                className="btn btn-primary"
                testId="launch-service-button"
              >
                {t('children.activity_sessions.credentials_modal.goto_button', { service: serviceName })}
              </ExternalLink>
            )}

            {/* Dual mode: dropdown button to switch between web and app */}
            {hasDualMode && (
              <div className="btn-group dropdown" data-testid="goto-dropdown">
                <ExternalLink
                  href={currentUrl}
                  className="btn btn-primary"
                  testId="launch-service-button"
                >
                  <i className={`bi ${urlPreference === 'app' ? 'bi-phone' : 'bi-globe'} me-1`}></i>
                  {t('children.activity_sessions.credentials_modal.goto_button', { service: serviceName })}
                </ExternalLink>
                <button
                  type="button"
                  className="btn btn-primary dropdown-toggle dropdown-toggle-split"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDropdown(!showDropdown);
                  }}
                  aria-expanded={showDropdown}
                  data-testid="goto-dropdown-toggle"
                >
                  <span className="visually-hidden">Toggle Dropdown</span>
                </button>
                {showDropdown && (
                  <ul className="dropdown-menu dropdown-menu-end show" style={{ position: 'absolute', right: 0, top: '100%' }}>
                    <li>
                      <ExternalLink
                        href={activityUrl}
                        className={`dropdown-item ${urlPreference === 'web' ? 'active' : ''}`}
                        testId="dropdown-web-option"
                        onClick={() => savePreference('web')}
                      >
                        <i className="bi bi-globe me-2"></i>
                        {t('children.activity_sessions.credentials_modal.use_web')}
                      </ExternalLink>
                    </li>
                    <li>
                      <ExternalLink
                        href={activityAppUrl}
                        className={`dropdown-item ${urlPreference === 'app' ? 'active' : ''}`}
                        testId="dropdown-app-option"
                        onClick={() => savePreference('app')}
                      >
                        <i className="bi bi-phone me-2"></i>
                        {t('children.activity_sessions.credentials_modal.use_app')}
                      </ExternalLink>
                    </li>
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Generic Instructions Modal */}
      {instructionsConfig && (
        <InstructionsModal
          show={showInstructions}
          onHide={() => setShowInstructions(false)}
          images={instructionsConfig.images}
          title={instructionsConfig.title}
          testId={instructionsConfig.testId}
        />
      )}
    </div>
  );
};
